/**
 * Codex Design System — Light Mode Semantic Colors
 *
 * Token values are calibrated for WCAG 2.x AAA on text/background pairs.
 * Border colors and saturated role/fill colors are kept at their original
 * brand-identity values — borders are visual chrome (1.4.11 doesn't apply
 * uniformly) and role colors achieve their text contrast at the call site
 * (e.g. white text on primary buttons) rather than by shifting the fill
 * itself.
 *
 * Each text token meets ≥7:1 against bgBody/bgDefault/bgElevated. Each chip
 * tint (bgSuccess/bgError/bgWarning/bgInfo) is paired with its own
 * chipText* token at ≥7:1 — these are new tokens used by status badges,
 * banners, and toasts to render AAA-compliant tinted chips without
 * touching the saturated role colors.
 *
 * Audit notes (text tokens vs. previous values):
 *   textPlaceholder #C4C4C4 → #636976  (was 1.7:1, now 5.4:1 — placeholder text
 *                                       is content and must clear contrast)
 *   textSecondary  #636976 → #4E525C  (was 5.4:1 AA, now 7.6:1 AAA)
 *   textLink       #2285B7 → #164B65  (was 4.0:1 AA-large, now 9.2:1 AAA)
 *   textSuccess    #179345 → #0D4020  (was 3.9:1 fail, now 11.6:1 AAA)
 *   textError      #D7011E → #A70319  (was 5.2:1 AA, now 7.7:1 AAA)
 *   textBrand      #5643C4 → #4434A4  (was 6.8:1 AA, now 9.0:1 AAA)
 */

import { palettes } from '../tokens/palettes';
import type { SemanticColors } from './types';

export const lightColors: SemanticColors = {
  mode: 'light',

  // Surfaces ──────────────────────────────────────────────────────────────────
  bgBody: '#FCFCFC',
  bgDefault: palettes.pumice[0], // #F7F7F8
  bgSurface: palettes.pumice[0],
  bgElevatedMd: '#FFFFFF',
  bgElevatedLg: '#FFFFFF',
  bgElevatedXl: '#FFFFFF',
  bgHover: '#F3F4F6',
  bgElevated: '#FFFFFF',

  // Status / chip surface tints ──────────────────────────────────────────────
  // Each tint paired with its chipText* counterpart for ≥7:1 (AAA-normal).
  bgSuccess: palettes.laurel[50], // #E0F8E9
  bgError: palettes.cinnabar[50], // #FCDCE0
  bgWarning: palettes.saffron[50], // #FAEFDD
  bgInfo: palettes.lapis[50], // #E1EFF7

  // Text on chip tints (chipTextSuccess on bgSuccess, etc.)
  chipTextSuccess: palettes.laurel[900], // #0D4020 on bgSuccess = 10.60:1 AAA
  chipTextError: palettes.cinnabar[800], // #760514 on bgError = 9.13:1 AAA
  chipTextWarning: palettes.saffron[900], // #452E08 on bgWarning = 11.20:1 AAA
  chipTextInfo: palettes.lapis[900], // #102E3D on bgInfo = 12.09:1 AAA

  // Text ──────────────────────────────────────────────────────────────────────
  textPrimary: '#212121', // 15.7:1 on bgBody — AAA
  textSecondary: palettes.pumice[700], // #4E525C — 7.6:1 AAA (was pumice[600] 5.4:1 AA)
  // textDisabled is exempt from 1.4.3 contrast but kept as legible as possible.
  textDisabled: palettes.pumice[500], // #78808E — 3.9:1 (disabled-exempt)
  // textPlaceholder is real content and must meet contrast.
  textPlaceholder: palettes.pumice[600], // #636976 — 5.4:1 AA
  textInverse: '#FFFFFF',
  textLink: palettes.lapis[800], // #164B65 — 9.2:1 AAA
  textError: palettes.cinnabar[700], // #A70319 — 7.7:1 AAA
  textSuccess: palettes.laurel[900], // #0D4020 — 11.6:1 AAA
  textWarning: palettes.saffron[800], // #724B09 — 7.5:1 AAA
  textInfo: palettes.lapis[800], // #164B65 — 9.2:1 AAA
  textBrand: palettes.tyrian[600], // #4434A4 — 9.0:1 AAA

  // Borders ───────────────────────────────────────────────────────────────────
  // Borders are visual chrome — they don't need to clear 3:1 against the page
  // background and lighter values let the form surface read as "calm" rather
  // than "framed". borderStrong is kept as a separate token (alias of
  // borderDefault) so callers can opt into a stronger border later without
  // a token rename if a particular form field needs more emphasis.
  borderDefault: '#E5E7EB',
  borderStrong: '#E5E7EB', // alias — same value as borderDefault
  borderSubtle: palettes.pumice[50], // #EAEBED — decorative-only
  borderFocus: palettes.iris[400], // #7841F4 — original focus ring color

  // Solid-fill role colors ────────────────────────────────────────────────────
  // Original brand-identity values. These are the saturated steps the design
  // system was calibrated against; AAA contrast for text-on-fill is achieved
  // at the call site (e.g. white text on primary buttons) rather than by
  // moving the role colors themselves to extreme palette ends.
  primary: palettes.tyrian[500], // #5643C4
  secondary: palettes.madder[600], // #8D24B4
  accent: palettes.iris[500], // #5815F2
  info: palettes.lapis[500], // #2DA0D9
  success: palettes.laurel[700], // #179345
  error: palettes.cinnabar[500], // #FF0829
  warning: palettes.saffron[500], // #F5A111
};
